import { ProviderInterface } from './../../model/user/provider-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    Subscription,
    Unsubscribable
} from 'rxjs';
import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { OrganisationMenuTabs } from 'src/app/model/enums/organisationMenuTabs';
import { OrganisationType } from 'src/app/model/enums/organisationType';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router, NavigationEnd } from '@angular/router';
import { API_BASE_FILESERVICE, API_MAP } from 'src/environments/api';
import { environment } from 'src/environments/environment';
import { UserService } from '../user-service/userservice';
import { map } from 'rxjs/operators';
import { filter, debounceTime } from 'rxjs/operators';
import { ProductService } from '../dataServices/product-service/productService';
import { UtilService } from '../util-service/utilService';
import { CertificationService } from '../dataServices/certification-service/certificationService';

@Injectable({
    providedIn: 'root'
})
export class HeaderServiceService {
    public activeOrgSub: Unsubscribable;
    public activeOrganisation: OrganisationInterface;

    private activeHeaderSubject = new BehaviorSubject<String>('Marktplatz');
    public activeHeaderObservable$: Observable<String> =
        this.activeHeaderSubject.asObservable();

    private providersSubject = new BehaviorSubject<ProviderInterface[]>([]);
    public providersObservable$: Observable<ProviderInterface[]> =
        this.providersSubject.asObservable();

    private activeProviderSubject = new BehaviorSubject<ProviderInterface>({});
    private activeProviderUserObservable$: Observable<ProviderInterface> =
        this.activeProviderSubject.asObservable();
    public activeProviderObservable$: Observable<ProviderInterface>;

    private menuTabs: OrganisationMenuTabs[] = [OrganisationMenuTabs.SETTINGS];
    public activeTabIndex: number = 0;

    constructor(
        private organisationService: OrganisationService,
        private userService: UserService,
        private router: Router,
        private productService: ProductService,
        private utilService: UtilService,
        private certificationService: CertificationService
    ) {
        this.activeOrgSub =
            this.organisationService.activeItemObserver.subscribe((org) => {
                if (org) {
                    this.activeOrganisation = org;
                    this.setMenuTabOptions(this.activeOrganisation.organisationType);
                }
            });
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((_event: NavigationEnd) => {
                if (this.activeOrganisation) {
                    this.setActiveTabFromCurrentRoute();
                }
            });
        this.providersObservable$ = combineLatest([
            this.userService.currentUserObservable,
            this.userService.currentMembershipsObserver
        ]).pipe(
            map(([user, memberships]) => {
                const confirmedMemberships = (memberships ?? []).filter(
                    m => m.membershipStatus === 'CONFIRMED'
                );

                // Case 1: there are confirmed orgs -> only orgs
                if (confirmedMemberships.length > 0) {
                    return confirmedMemberships.map((membership) => {
                        const image = membership.fileId
                            ? API_BASE_FILESERVICE + API_MAP['files']['GET'].replace(':fileId', membership.fileId)
                            : environment.defaultPictureOrganisationProducts;

                        const updatedMembership: ProviderInterface = {
                            name: membership.organisationName,
                            img: image,
                            organisationId: membership.organisationId
                        };
                        return updatedMembership;
                    });
                }
                if (user) {
                    const image = user.fileId
                        ? API_BASE_FILESERVICE + API_MAP['files']['GET'].replace(':fileId', user.fileId)
                        : environment.defaultPictureProfile;

                    const updatedUser: ProviderInterface = {
                        name: `${user.firstName} ${user.lastName}`,
                        img: image
                    };
                    return [updatedUser];
                }
                return [];
            })
        );
        this.activeProviderObservable$ = combineLatest([
            this.providersObservable$,
            this.organisationService.activeItemObserver,
            this.activeProviderUserObservable$
        ]).pipe(
            map(([providers, activeOrganisation, activeProviderUser]) => {
                // TODO: really always the first provider as default? or better last active one?
                let updatedActiveProvider: ProviderInterface | undefined =
                    providers && providers.length > 0 ? providers[0] : undefined;
                if (activeOrganisation && providers && providers.length > 0) {
                    const indexInProviders = providers.findIndex(
                        (provider) =>
                            provider.organisationId === activeOrganisation.organisationId
                    );
                    if (indexInProviders !== -1) {
                        updatedActiveProvider = providers[indexInProviders];
                    }
                }
                if (activeProviderUser && providers && providers.length > 0) {
                    const indexInProviders = providers.findIndex(
                        (provider) => provider.name === activeProviderUser.name
                    );
                    if (indexInProviders !== -1) {
                        updatedActiveProvider = providers[indexInProviders];
                    }
                }
                if (updatedActiveProvider && updatedActiveProvider.name) {
                    this.loadHeaderText(this.setHeaderText(updatedActiveProvider.name));
                } else {
                }
                return updatedActiveProvider ?? { name: '', img: '' } as ProviderInterface;
            })
        );
    }

    private setHeaderText(userName: string): string {
        let tmpHeader: string = this.router.url.split('?')[0];

        if (tmpHeader.includes('/privacy')) {
            tmpHeader = 'Datenschutzerklärung';
        } else if (tmpHeader == '/impressum') {
            tmpHeader = 'Impressum';
        } else if (tmpHeader == '/marktplatz') {
            tmpHeader = 'Marktplatz';
        } else {
            tmpHeader = userName;
        }

        return tmpHeader;
    }
    loadHeaderText(text: string) {
        this.activeHeaderSubject.next(text);
    }

    public getMenuTabs(): OrganisationMenuTabs[] {
        return this.menuTabs;
    }

    public setMenuTabs(newTabs: OrganisationMenuTabs[]) {
        this.menuTabs = newTabs;
    }

    /**
     * Set the tab options dependent on the organisationType: e.g. "Produkte"-Tab for product provider organisations.
     * If the user is the activeProvider this function will not be called as the user has no organisationId. The only available tab then is "Einstellungen"
     * @param organisationType
     */
    public setMenuTabOptions(organisationType: OrganisationType[]): void {
        const types = organisationType ?? [];
        const tabs = new Set<OrganisationMenuTabs>([
            OrganisationMenuTabs.SETTINGS
        ]);

        if (
            types.includes(OrganisationType.PRODUCT_PROVIDER) ||
            types.includes(OrganisationType.DATASPACE_PARTICIPANT)
        ) {
            tabs.add(OrganisationMenuTabs.PRODUCTS);
        }
        if (types.includes(OrganisationType.PRODUCT_TYPE_ASSOCIATION)) {
            tabs.add(OrganisationMenuTabs.PRODUCT_TYPES);
            tabs.add(OrganisationMenuTabs.ATTRIBUTES);
        }
        if (
            types.includes(OrganisationType.CERTIFICATION_PROVIDER) ||
            types.includes(OrganisationType.CERTIFICATION_OFFICE)
        ) {
            tabs.add(OrganisationMenuTabs.CERTIFICATIONS);
        }

        this.setMenuTabs(Array.from(tabs));
    }

    /**
     * The user is the only provider that doesnt have an organisationId in the providers-Array: If no organisationId is present, route to user settings
     * @param organisationId
     */
    public openSettings(organisationId?: number): void {
        const currentUrl = this.router.url;
        if (organisationId) {
            if (!currentUrl.includes('firstProduct') && !currentUrl.includes('aboPayment')) {
                if (currentUrl !== '/organisation') {
                    this.router.navigate([
                        'organisations/' + organisationId + '/information'
                    ]);
                }
            } else if (currentUrl.includes('firstProduct')) {
                this.router.navigate([
                    'organisations/' + organisationId + 'firstProduct'
                ]);
            } else if (currentUrl.includes('aboPayment/confirmation')) {
                this.router.navigate([
                    'organisations/' + organisationId + 'aboPayment/confirmation'
                ]);
            }
        } else {
            this.router.navigate(['profil/settings']);
        }
        this.activeTabIndex = 0;
    }

    setActiveProvider(
        newActiveProvider: ProviderInterface,
        backToOverview?: boolean
    ) {
        this.activeProviderSubject.next(newActiveProvider);
        if (!backToOverview)
            this.openSettings(newActiveProvider.organisationId);
        if (newActiveProvider.organisationId) {
            this.organisationService.setActiveOrganisation(
                newActiveProvider.organisationId
            );
        } else if (backToOverview) {
            // auf dem Marktplatz aktives profil immer der user

            this.router.navigate(['/']);
        } else {
            //No organisationId --> user is activeProvider and no tabs should be available.

            //this.setMenuTabs([OrganisationMenuTabs.SETTINGS]);
            this.setMenuTabs([]);
        }
        if (!backToOverview) this.activeTabIndex = 0;

        this.loadHeaderText(newActiveProvider.name);
    }

    public setActiveProviderWithoutNavigation(newActiveProvider: ProviderInterface): void {
        this.activeProviderSubject.next(newActiveProvider);

        if (newActiveProvider.organisationId) {
            this.organisationService.setActiveOrganisation(
                newActiveProvider.organisationId
            );
            this.activeTabIndex = 0;
        }

        this.loadHeaderText(newActiveProvider.name);
    }

    public changedTab(event: MatTabChangeEvent): void {
        this.activeTabIndex = event.index;
        let activeMenuTab: OrganisationMenuTabs =
            this.getMenuTabs()[event.index];
        switch (activeMenuTab) {
            case OrganisationMenuTabs.SETTINGS: {
                this.openSettings(this.activeOrganisation.organisationId);
                break;
            }
            case OrganisationMenuTabs.PRODUCTS: {
                const firstProductId = this.handleFirstOrganisationProduct();
                const productPath = firstProductId ? `/${firstProductId}` : '';

                const baseUrl = `/organisations/${this.activeOrganisation.organisationId}/products${productPath}`;
                const currentUrl = this.router.url;

                if (currentUrl === baseUrl || !currentUrl.startsWith(baseUrl + '/')) {
                    this.router.navigate([baseUrl]);
                }
                break;
            }
            case OrganisationMenuTabs.CERTIFICATIONS: {
                // this.router.navigate([
                //     '/organisations/' +
                //         this.activeProviderSubject.value.organisationId +
                //         '/certifications'
                // ]);
                // break;
                const firstCertificationId = this.handleFirstOrganisationCertification();
                const certificationPath = firstCertificationId ? `/${firstCertificationId}` : '';

                const baseUrl = `/organisations/${this.activeOrganisation.organisationId}/certifications${certificationPath}`;
                const currentUrl = this.router.url;

                if (currentUrl === baseUrl || !currentUrl.startsWith(baseUrl + '/')) {
                    this.router.navigate([baseUrl]);
                }
                break;
            }
            case OrganisationMenuTabs.ATTRIBUTES: {
                this.router.navigate([
                    '/organisations/' +
                    this.activeProviderSubject.value.organisationId +
                    '/features'
                ]);
                break;
            }
            case OrganisationMenuTabs.PRODUCT_TYPES: {
                this.router.navigate([
                    '/organisations/' +
                    this.activeProviderSubject.value.organisationId +
                    '/productTypeGroups'
                ]);
                break;
            }
            default: {
                this.openSettings(null);

                break;
            }
        }
    }

    /**
     * Sets the active Tab Index based on the actual route
     */
    private setActiveTabFromCurrentRoute(): void {
        const currentUrl = this.router.url;
        let targetTab: OrganisationMenuTabs;

        if (currentUrl.includes('/products')) {
            targetTab = OrganisationMenuTabs.PRODUCTS;
        } else if (currentUrl.includes('/certifications')) {
            targetTab = OrganisationMenuTabs.CERTIFICATIONS;
        } else if (currentUrl.includes('/features')) {
            targetTab = OrganisationMenuTabs.ATTRIBUTES;
        } else if (currentUrl.includes('/productTypeGroups')) {
            targetTab = OrganisationMenuTabs.PRODUCT_TYPES;
        } else if (currentUrl.includes('/information') || currentUrl.includes('/organisations/')) {
            targetTab = OrganisationMenuTabs.SETTINGS;
        } else {
            this.activeTabIndex = 0;
            return;
        }

        this.activeTabIndex = this.menuTabs.findIndex(tab => tab === targetTab);

        if (this.activeTabIndex === -1) {
            this.activeTabIndex = 0;
        }
    }

    private handleFirstOrganisationProduct() {
        const organisationProducts = this.productService.getProductsOfOrganisation(
            this.activeOrganisation.organisationId
        );
        this.utilService.sortByName(organisationProducts);

        return organisationProducts[0]?.id;
    }

    private handleFirstOrganisationCertification() {
        const organisationCertifications = this.getCertificationsOfOrganisation(
            this.activeOrganisation.organisationId
        );
        this.utilService.sortByName(organisationCertifications);

        return organisationCertifications[0]?.id;
    }

    private getCertificationsOfOrganisation(organisationId: number) {
        return this.certificationService.certifications.filter(cert =>
            cert.company === organisationId
        );
    }

}
